import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  downloadVendorBatchLabelsPdf,
  downloadVendorBatchOrdersPdf,
  downloadVendorBatchOrdersTxt,
  getVendorBatch,
  markVendorBatchSent,
} from '../../../endpoints/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const formatBatchType = (batchType?: string) => {
  switch (batchType) {
    case 'chapter_pins':
      return 'Chapter pins';
    case 'state_pins':
      return 'State pins';
    default:
      return batchType || '—';
  }
};

export default function VendorOrderBatchDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [ordersPdfBusy, setOrdersPdfBusy] = React.useState(false);
  const [labelsPdfBusy, setLabelsPdfBusy] = React.useState(false);
  const [txtBusyLineType, setTxtBusyLineType] = React.useState<string | null>(
    null
  );
  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-vendor-batch', id],
    () => getVendorBatch(id!),
    { enabled: !!id, retry: 1 }
  );

  const handleMarkSent = async () => {
    if (!id) return;
    setActionError(null);
    setActionBusy(true);
    try {
      await markVendorBatchSent(id);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batch', id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-management-orders'],
        }),
      ]);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to mark batch sent.'
      );
    } finally {
      setActionBusy(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadOrdersPdf = async () => {
    if (!id) return;
    setActionError(null);
    setOrdersPdfBusy(true);
    try {
      const blob = await downloadVendorBatchOrdersPdf(id);
      downloadBlob(blob, `vendor-batch-${id}-orders.pdf`);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to download orders PDF.'
      );
    } finally {
      setOrdersPdfBusy(false);
    }
  };

  const handleDownloadLabelsPdf = async () => {
    if (!id) return;
    setActionError(null);
    setLabelsPdfBusy(true);
    try {
      const blob = await downloadVendorBatchLabelsPdf(id);
      downloadBlob(blob, `vendor-batch-${id}-labels.pdf`);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to download labels PDF.'
      );
    } finally {
      setLabelsPdfBusy(false);
    }
  };

  const handleDownloadOrdersTxt = async (lineType: string) => {
    if (!id) return;
    setActionError(null);
    setTxtBusyLineType(lineType);
    try {
      const blob = await downloadVendorBatchOrdersTxt(id, lineType);
      downloadBlob(blob, `vendor-batch-${id}-${lineType}.txt`);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to download orders TXT.'
      );
    } finally {
      setTxtBusyLineType(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={data ? `Vendor batch ${data.id}` : 'Vendor batch'}
        subtitle="Review included orders and line personalization before sending work to the vendor."
        leftDom={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate('/chapter-supplies-management/vendor-batches')
            }
          >
            Back to vendor batches
          </Button>
        }
        rightDom={
          data?.batch_status !== 'sent' ? (
            <Button
              variant="contained"
              disabled={actionBusy}
              onClick={() => void handleMarkSent()}
            >
              {actionBusy ? 'Marking…' : 'Mark sent to vendor'}
            </Button>
          ) : undefined
        }
      />

      {isLoading && <Typography>Loading vendor batch…</Typography>}
      {error && <Alert severity="error">Unable to load vendor batch.</Alert>}
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {data && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Batch metadata
              </Typography>
              <Typography variant="body2">
                Vendor: {data.vendor_name || data.vendor_id}
                <br />
                Type: {formatBatchType(data.batch_type)}
                <br />
                PO: {data.po_number || '—'}
                <br />
                Status: {data.batch_status}
                <br />
                Created: {new Date(data.created_at).toLocaleString()}
                <br />
                Orders: {data.order_count}
                <br />
                Lines: {data.line_count}
                <br />
                Emblems (qty):{' '}
                {data.total_emblem_quantity != null
                  ? data.total_emblem_quantity
                  : '—'}
                {data.sent_at ? (
                  <>
                    <br />
                    Sent: {new Date(data.sent_at).toLocaleString()}
                  </>
                ) : null}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Documents
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {data.orders_pdf_endpoint ? (
                  <Button
                    variant="outlined"
                    disabled={ordersPdfBusy}
                    onClick={() => void handleDownloadOrdersPdf()}
                  >
                    {ordersPdfBusy ? 'Downloading…' : 'Download orders PDF'}
                  </Button>
                ) : null}
                {data.labels_pdf_endpoint ? (
                  <Button
                    variant="outlined"
                    disabled={labelsPdfBusy}
                    onClick={() => void handleDownloadLabelsPdf()}
                  >
                    {labelsPdfBusy ? 'Downloading…' : 'Download labels PDF'}
                  </Button>
                ) : null}
                {data.txt_documents?.map((doc) => (
                  <Button
                    key={doc.line_type}
                    variant="outlined"
                    disabled={txtBusyLineType === doc.line_type}
                    onClick={() => void handleDownloadOrdersTxt(doc.line_type)}
                  >
                    {txtBusyLineType === doc.line_type
                      ? 'Downloading…'
                      : `Download ${doc.label} TXT`}
                  </Button>
                ))}
                {!data.orders_pdf_endpoint && !data.labels_pdf_endpoint ? (
                  <Typography variant="body2" color="text.secondary">
                    Document downloads are not available for this batch type
                    yet.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Orders
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Placed</TableCell>
                    <TableCell>Purchaser</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ship to</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.orders.map((order) => (
                    <TableRow
                      hover
                      key={order.order_id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `/chapter-supplies-management/orders/${order.order_id}`
                        )
                      }
                    >
                      <TableCell>
                        {order.order_number != null
                          ? `#${order.order_number}`
                          : order.order_id}
                      </TableCell>
                      <TableCell>
                        {new Date(order.placed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{order.purchaser_display_name}</TableCell>
                      <TableCell>{order.order_status_name}</TableCell>
                      <TableCell>{order.ship_to_name}</TableCell>
                      <TableCell align="right">
                        {formatPrice(order.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Batch lines
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Custom text</TableCell>
                    <TableCell>Personalization</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line) => (
                    <TableRow key={line.order_line_item_id}>
                      <TableCell>
                        {line.order_number != null
                          ? `#${line.order_number}`
                          : line.order_id}
                      </TableCell>
                      <TableCell>
                        {line.item_display_name || line.item_name}
                        {line.sku ? (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {line.sku}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">
                        {formatPrice(line.unit_price)}
                      </TableCell>
                      <TableCell>
                        {line.custom_text_lines &&
                        line.custom_text_lines.length > 0
                          ? line.custom_text_lines.join(', ')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {line.personalization_display_name ||
                        line.personalization_first_name ? (
                          <>
                            {line.personalization_display_name
                              ? `Display: ${line.personalization_display_name}`
                              : ''}
                            {line.personalization_display_name &&
                            line.personalization_first_name
                              ? ' | '
                              : ''}
                            {line.personalization_first_name
                              ? `First: ${line.personalization_first_name}`
                              : ''}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
